import { createContext, type MutableRefObject, useContext } from 'react';

/**
 * `menu` holds items and gets the full menu keyboard. `panel` holds a form of its own and
 * leaves its keys to the browser. `list` is a panel whose content is a list of controls,
 * such as checkboxes: the trigger's arrow keys open it, and a keyboard open lands on its
 * first control.
 */
export type MenuKind = 'menu' | 'panel' | 'list';

/** Where focus goes once a keyboard-opened menu has rendered its items. */
export type PendingFocus = 'first' | 'last' | null;

export type MenuDirection = 'down' | 'up' | 'left' | 'right';

export interface MenuContextValue {
    kind: MenuKind;
    isOpen: boolean;
    direction: MenuDirection;
    /** Asks for the menu open or closed; asking for the state it is already in does nothing. */
    setOpen: (open: boolean) => void;
    triggerRef: MutableRefObject<HTMLElement | null>;
    /** Ids for the trigger and the popup, free of the colons Radix's own ids hold. */
    triggerId: string;
    contentId: string;
    /** The popup while it is open. */
    contentRef: MutableRefObject<HTMLElement | null>;
    pendingFocus: MutableRefObject<PendingFocus>;
}

export const MenuContext = createContext<MenuContextValue | null>(null);

export function useMenuContext(component: string): MenuContextValue {
    const context = useContext(MenuContext);
    if (!context) {
        throw new Error(`${component} must be rendered inside a Menu`);
    }
    return context;
}

export const ITEM_SELECTOR =
    '[role="menuitem"],[role="menuitemcheckbox"],[role="menuitemradio"],[role="option"]';

/** The items keyboard focus may land on, in order. */
export function menuItems(menuElement: HTMLElement): HTMLElement[] {
    return Array.from(menuElement.querySelectorAll<HTMLElement>(ITEM_SELECTOR)).filter(
        (item) => item.getAttribute('aria-disabled') !== 'true' && !item.hasAttribute('disabled')
    );
}

const CONTROL_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]';

/**
 * The controls of a list, in order: whatever the Tab key reaches. A hidden form input a
 * Radix checkbox keeps beside its button is left out by its tabindex.
 */
export function listControls(menuElement: HTMLElement): HTMLElement[] {
    return Array.from(menuElement.querySelectorAll<HTMLElement>(CONTROL_SELECTOR)).filter(
        (control) =>
            control.tabIndex >= 0 &&
            !control.hasAttribute('disabled') &&
            control.getAttribute('aria-hidden') !== 'true'
    );
}

/** Focuses the item a keyboard open asked for, once the menu has rendered it. */
export function focusPendingItem(menu: MenuContextValue): void {
    const menuElement = menu.contentRef.current;
    const pending = menu.pendingFocus.current;
    if (!menuElement || pending === null) return;
    const items = menu.kind === 'list' ? listControls(menuElement) : menuItems(menuElement);
    const target = pending === 'last' ? items[items.length - 1] : items[0];
    if (target) {
        target.focus();
        menu.pendingFocus.current = null;
    }
}
